import { useState } from "react";
import ModalLayout from "../layouts/ModalLayout";
import authApis from "../../api/authApis";
import { useSelector } from "react-redux";
import { RootState } from "../../store";

interface Props {
  onClose?: () => void;
  userId?: number;
}

function UpdatePassword({ onClose, userId }: Props) {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const loggedInUser = useSelector((state: RootState) => state.access.staff);

  const effectiveUserId = userId ?? loggedInUser?.userId;
  const isSelf = loggedInUser?.userId === effectiveUserId;

  const handleSubmit = async () => {
    setError(null);
    setSuccess(null);

    if (!newPassword || !confirmPassword || (isSelf && !oldPassword)) {
      setError("All required fields must be filled.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("New password and confirm password do not match.");
      return;
    }

    try {
      setLoading(true);

      const res = await authApis.changePassword({
        userId: effectiveUserId,
        oldPassword: isSelf ? oldPassword : undefined,
        newPassword,
      });

      if (res?.status === 1) {
        setSuccess("Password updated successfully.");
        setOldPassword("");
        setNewPassword("");
        setConfirmPassword("");
      } else {
        setError(res?.error?.message || "Failed to update password.");
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalLayout onClose={onClose} width="max-w-[24rem]">
      <div className="p-6 space-y-4">
        <h2 className="text-xl font-semibold text-center">Update Password</h2>

        {/* Old Password */}
        {isSelf && (
          <div>
            <label className="block text-sm font-medium mb-1">
              Old Password
            </label>
            <div className="relative">
              <input
                type={showOld ? "text" : "password"}
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter old password"
              />
              <button
                type="button"
                onClick={() => setShowOld(!showOld)}
                className="absolute right-3 top-2 text-sm text-gray-500"
              >
                {showOld ? "Hide" : "Show"}
              </button>
            </div>
          </div>
        )}

        {/* New Password */}
        <div>
          <label className="block text-sm font-medium mb-1">New Password</label>
          <div className="relative">
            <input
              type={showNew ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter new password"
            />
            <button
              type="button"
              onClick={() => setShowNew(!showNew)}
              className="absolute right-3 top-2 text-sm text-gray-500"
            >
              {showNew ? "Hide" : "Show"}
            </button>
          </div>
        </div>

        {/* Confirm Password */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Confirm Password
          </label>
          <div className="relative">
            <input
              type={showConfirm ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Confirm new password"
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              className="absolute right-3 top-2 text-sm text-gray-500"
            >
              {showConfirm ? "Hide" : "Show"}
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && <p className="text-sm text-red-500 text-center">{error}</p>}

        {/* Success Message */}
        {success && (
          <p className="text-sm text-green-600 text-center">{success}</p>
        )}

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={loading}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg transition disabled:opacity-50"
        >
          {loading ? "Updating..." : "Update Password"}
        </button>
      </div>
    </ModalLayout>
  );
}

export default UpdatePassword;
